import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";

export type CalendarDisplayMetricVisibilityContext = {
    statusId?: string | null;
};

export type CalendarDisplayMetricColumnRef =
    | {kind: "system"; columnId: "createdAt" | "updatedAt"}
    | {kind: "eventAdditionalColumn"; columnId: Uuid}
    | {kind: "journeyAdditionalColumn"; columnId: Uuid}
    | {
          kind: "custom";
          /** Column id sent to the API for visible-range date filters. */
          filterColumnId: string;
          resolveIsoStart: (row: unknown) => string | undefined;
      };

export type CalendarDisplayMetricOption = {
    id: string;
    label: string;
    columnRef: CalendarDisplayMetricColumnRef;
    isVisible?: (ctx: CalendarDisplayMetricVisibilityContext) => boolean;
};

export type CalendarDisplayMetricsConfig = {
    options: Array<CalendarDisplayMetricOption>;
    defaultMetricId: string;
};

export function getVisibleCalendarDisplayMetricOptions(
    config: CalendarDisplayMetricsConfig,
    ctx: CalendarDisplayMetricVisibilityContext,
): Array<CalendarDisplayMetricOption> {
    return config.options.filter((option) => option.isVisible?.(ctx) !== false);
}

export function resolveCalendarDisplayMetricFilterColumnId(columnRef: CalendarDisplayMetricColumnRef): string {
    switch (columnRef.kind) {
        case "system":
            return columnRef.columnId;
        case "eventAdditionalColumn":
        case "journeyAdditionalColumn":
            return String(columnRef.columnId);
        case "custom":
            return columnRef.filterColumnId;
    }
}

export function getAllCalendarDisplayMetricFilterColumnIds(config: CalendarDisplayMetricsConfig): Array<string> {
    const ids = new Set<string>();
    for (const option of config.options) {
        ids.add(resolveCalendarDisplayMetricFilterColumnId(option.columnRef));
    }
    return [...ids];
}

export function resolveDefaultCalendarDisplayMetricId(
    config: CalendarDisplayMetricsConfig,
    ctx: CalendarDisplayMetricVisibilityContext,
): string {
    const visibleOptions = getVisibleCalendarDisplayMetricOptions(config, ctx);
    if (visibleOptions.some((option) => option.id === config.defaultMetricId) === true) {
        return config.defaultMetricId;
    }
    return visibleOptions[0]?.id ?? config.defaultMetricId;
}

export function findCalendarDisplayMetricOption(
    config: CalendarDisplayMetricsConfig,
    metricId: string,
    ctx: CalendarDisplayMetricVisibilityContext,
): CalendarDisplayMetricOption | undefined {
    return getVisibleCalendarDisplayMetricOptions(config, ctx).find((option) => option.id === metricId);
}
